import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import { useMemo } from "react";
import { useRealmApp } from "../RealmApp";

function findAndAdd(sourceArray, destinationArray) {
  if (!sourceArray) return [];
  const resultArray = [];
  sourceArray.forEach((itemId) => {
    const itemFound = destinationArray.find((item) => item._id === itemId);
    if (itemFound) resultArray.push(itemFound);
  });
  return resultArray;
}

/*
* export type ZXReportItemType =
  | 'payment'
  | 'item'
  | 'user'
  | 'commission'
  | 'withdraw'
  | 'deposit'
  | 'tax';
*/
const useZReportItems = (zReport) => {
  const app = useRealmApp();
  const { zReportItems, loading } = useAllZReportItems(
    app.currentUser.id,
    zReport.storeId,
    zReport._id
  );
  const [payments, taxes, subcategories, users, withdrawals, commissions] =
    useMemo(() => {
      const payments = findAndAdd(
        zReport.payments,
        zReportItems.filter((item) => item.type === "payment")
      );
      const taxes = findAndAdd(
        zReport.taxes,
        zReportItems.filter((item) => item.type === "tax")
      );
      const subcategories = findAndAdd(
        zReport.subcategories,
        zReportItems.filter((item) => item.type === "item")
      );
      const users = findAndAdd(
        zReport.users,
        zReportItems.filter((item) => item.type === "user")
      );
      const withdrawals = findAndAdd(
        zReport.withdrawals,
        zReportItems.filter(
          (item) => item.type === "deposit" || item.type === "withdraw"
        )
      );
      const commissions = findAndAdd(
        zReport.commissions,
        zReportItems.filter((item) => item.type === "commission")
      );
      return [payments, taxes, subcategories, users, withdrawals, commissions];
    }, [zReport, zReportItems]);
  return {
    loading,
    payments,
    taxes,
    subcategories,
    users,
    withdrawals,
    commissions,
  };
};

export default useZReportItems;

function useAllZReportItems(ownerId, storeId, reportId) {
  const variables = {
    ownerId: `owner_id=${ownerId}&store_id=${storeId}`,
    reportId,
  };
  //
  const { data, loading, error } = useQuery(
    gql`
      query getZReportItems($ownerId: String!, $reportId: ObjectId!) {
        zReportItems(query: { owner_id: $ownerId, reportId: $reportId }) {
          _id
          amount
          amount2
          amount3
          code
          creationDatetime
          description
          groupDescription
          groupId
          itemId
          quantity
          quantity2
          quantity3
          reportId
          storeId
          terminalId
          terminalSerial
          type
          updateDatetime
        }
      }
    `,
    { variables }
  );
  //
  if (error) {
    throw new Error(`Failed to fetch tasks: ${error.message}`);
  }
  // If the query has finished, return the tasks from the result data
  // Otherwise, return an empty list
  let zReportItems = useMemo(() => data?.zReportItems ?? [], [data]);

  if (!Array.isArray(zReportItems)) {
    zReportItems = [zReportItems];
  }

  const sortedZReportItems = useMemo(() => {
    return zReportItems
      .slice()
      .sort((a, b) => new Date(b.updateDatetime) - new Date(a.updateDatetime));
  }, [zReportItems]);

  return { zReportItems: sortedZReportItems, loading };
}
